// Browser geolocation, fetched only when the host page already has permission.
// Never triggers a prompt from the widget. Always resolves null on any failure
// path so callers can fail open without a try/catch.

export type BrowserLocation = {
  latitude: number;
  longitude: number;
  accuracy: number;
};

const DEFAULT_TIMEOUT_MS = 1500;
const MAX_AGE_MS = 5 * 60 * 1000;

type PermissionsLike = {
  query: (descriptor: { name: PermissionName }) => Promise<{ state: PermissionState }>;
};

type GeolocationLike = {
  getCurrentPosition: (
    success: (pos: GeolocationPosition) => void,
    error: (err: GeolocationPositionError) => void,
    options?: PositionOptions,
  ) => void;
};

function readNavigator(): { permissions?: PermissionsLike; geolocation?: GeolocationLike } {
  if (typeof navigator === "undefined") return {};
  const nav = navigator as Navigator & { permissions?: PermissionsLike };
  return { permissions: nav.permissions, geolocation: nav.geolocation };
}

export async function getBrowserLocationIfGranted(
  opts: { timeoutMs?: number } = {},
): Promise<BrowserLocation | null> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const { permissions, geolocation } = readNavigator();
  if (!permissions || !geolocation) return null;

  try {
    const status = await permissions.query({ name: "geolocation" });
    if (status.state !== "granted") return null;
  } catch {
    return null;
  }

  return new Promise<BrowserLocation | null>((resolve) => {
    let settled = false;
    const settle = (value: BrowserLocation | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    // Fallback timer in case neither geolocation callback fires. The native
    // PositionOptions.timeout is supposed to handle this, but we don't trust
    // every browser implementation — symmetry with PLAYER_CONTEXT_TIMEOUT_MS.
    const fallback = setTimeout(() => settle(null), timeoutMs);

    geolocation.getCurrentPosition(
      (pos) => {
        clearTimeout(fallback);
        settle({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
      },
      () => {
        clearTimeout(fallback);
        settle(null);
      },
      { enableHighAccuracy: false, maximumAge: MAX_AGE_MS, timeout: timeoutMs },
    );
  });
}
